import React from 'react';
import { Link } from 'react-router-dom';
import { Download, ExternalLink, Mail, Calendar } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';

const PressPage = () => {
  const pressReleases = [
    {
      date: 'January 15, 2025',
      title: 'ToolsMetric Reaches 2,500+ Software Tools Milestone',
      excerpt: 'Platform continues rapid growth as businesses seek better software discovery solutions.'
    },
    {
      date: 'December 1, 2024',
      title: 'ToolsMetric Launches AI-Powered Tool Recommendations',
      excerpt: 'New feature uses machine learning to match businesses with their ideal software stack.'
    },
    {
      date: 'October 15, 2024',
      title: 'ToolsMetric Surpasses 100,000 Monthly Active Users',
      excerpt: 'Growing community of businesses rely on platform for software decisions.'
    }
  ];

  const mediaFeatures = [
    { outlet: 'TechCrunch', title: 'The Rise of Software Discovery Platforms' },
    { outlet: 'Forbes', title: 'How ToolsMetric is Changing Business Software Selection' },
    { outlet: 'VentureBeat', title: 'Top Platforms for Finding Business Tools in 2025' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <main className="pt-24 pb-16">
        {/* Hero */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Press & Media
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
            Get the latest news about ToolsMetric, download brand assets, and find media contact information.
          </p>
          <div className="flex justify-center gap-4">
            <Button className="bg-emerald-500 hover:bg-emerald-600">
              <Download className="w-4 h-4 mr-2" /> Download Press Kit
            </Button>
            <Button variant="outline">
              <Mail className="w-4 h-4 mr-2" /> Media Inquiries
            </Button>
          </div>
        </section>

        {/* Press Releases */}
        <section className="bg-gray-50 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">Press Releases</h2>
            <div className="space-y-6">
              {pressReleases.map((release, index) => (
                <div key={index} className="bg-white rounded-xl p-6 border border-gray-100">
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                    <Calendar className="w-4 h-4" />
                    {release.date}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{release.title}</h3>
                  <p className="text-gray-600 mb-4">{release.excerpt}</p>
                  <Button variant="link" className="text-emerald-600 p-0 h-auto">
                    Read More <ExternalLink className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Media Features */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">In the Media</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {mediaFeatures.map((feature, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6">
                <div className="text-sm text-emerald-600 font-medium mb-2">{feature.outlet}</div>
                <h3 className="font-semibold text-gray-900">{feature.title}</h3>
              </div>
            ))}
          </div>
        </section>

        {/* Brand Assets */}
        <section className="bg-gray-50 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">Brand Assets</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-8 border border-gray-100">
                <h3 className="font-semibold text-gray-900 mb-4">Logos</h3>
                <p className="text-gray-600 mb-4">Download our logo in various formats and sizes.</p>
                <Button variant="outline">
                  <Download className="w-4 h-4 mr-2" /> Download Logos
                </Button>
              </div>
              <div className="bg-white rounded-xl p-8 border border-gray-100">
                <h3 className="font-semibold text-gray-900 mb-4">Brand Guidelines</h3>
                <p className="text-gray-600 mb-4">Our comprehensive brand style guide.</p>
                <Button variant="outline">
                  <Download className="w-4 h-4 mr-2" /> Download Guidelines
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl p-12 text-center text-white">
            <h2 className="text-2xl font-bold mb-4">Media Contact</h2>
            <p className="text-emerald-100 mb-6">
              For press inquiries, please contact our communications team.
            </p>
            <p className="text-white font-medium">press@toolsmetric.com</p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PressPage;